import { useEffect, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { ApiCall } from "../../../helper/axios";
import { toUrlVendor } from "../../../helper/vendorMask";
import { hotelQueryParams } from "./queryParamUtils";
import moment from "moment/moment.js";
import { useSelector } from "react-redux";
import { getHeaders } from "./useHotelDataMerger";

let hotelDetailsCache = new Map();
let hotelDetailsPromises = new Map();

export const useGetHotelDetails = () => {
  const [details, setData] = useState();
  const [loading, setLoading] = useState(true);
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const params = useParams();
  const [selectedOption, setSelectedOption] = useState(null);
  const [detailError,setdetailError]=useState(false)
  const location = useLocation();
  const [checkoutErr,setCheckoutErr]=useState(null)
  const navigate = useNavigate();
  const { agentActiveCurrency } = useSelector((state) => state.userData);
  const [header, setHeaders] = useState({})
  const {
    city,
    hid,
    ratings,
    nationality,
    countryOfResidence,
    roomInfo,
    checkinDate,
    checkoutDate,
    fsc,
    vendor,
    amount, commonId,residence, hrCityId
  } = hotelQueryParams(location);

  const refreshData = async () => {
    setLoading(true);
    try {
      const payload = {
        checkinDate: moment(checkinDate).format("YYYY-MM-DD"),
        checkoutDate: moment(checkoutDate).format("YYYY-MM-DD"),
        roomInfo,
        cityId: (vendor === "HOTELRACK" && hrCityId) ? hrCityId : city,
        nationalityId: nationality,
        countryOfResidence: countryOfResidence,
        ratings: (ratings ?? []).filter((d) => d),
        fsc: fsc,
        hid,
        source : vendor,
        vendor,
        amount,
        commonId,
        residence: residence
      };
      payload.activeCurrency = agentActiveCurrency;
      payload.hotelCodes = params.id;
      const cacheKey = `${params.id}_${JSON.stringify(payload)}`;
      if (hotelDetailsCache.has(cacheKey)) {
        const cachedData = hotelDetailsCache.get(cacheKey);
        const now = Date.now();
        if (now - cachedData.timestamp < 600000) {
          setTimeout(() => {
            setData(cachedData.data);
            const options = cachedData.data?.hotelDetails?.hotels?.[0]?.options;
            setSelectedOption(options?.[0]);
            setLoading(false);
          }, 2000);
          return;
        } else {
          hotelDetailsCache.delete(cacheKey);
        }
      }

      if (hotelDetailsPromises.has(cacheKey)) {
        try {
          const result = await hotelDetailsPromises.get(cacheKey);
          setTimeout(() => {
            setData(result);
            const options = result?.hotelDetails?.hotels?.[0]?.options;
            setSelectedOption(options?.[0]);
            setLoading(false);
          }, 2000);
          return;
        } catch (error) {
          hotelDetailsPromises.delete(cacheKey);
        }
      }

      const apiPromise = ApiCall("POST", "/hotels/details", payload);
      let status = await apiPromise
      if (status.status !== 200) {
        setdetailError(true)
        setLoading(false)
      }
      else {
        hotelDetailsPromises.set(cacheKey, apiPromise);
      }
      try {
        const response = await apiPromise;
        if (response?.status === 200 && response?.data?.statusCode === 200) {
          setData(response?.data?.data);
          const options = response?.data?.data?.hotelDetails?.custom;
          setSelectedOption(options);
          const cacheData = {
            timestamp: Date.now(),
            data: response?.data?.data
          };
          hotelDetailsCache.set(cacheKey, cacheData);
        }
      } finally {
        hotelDetailsPromises.delete(cacheKey);
      }
    } catch (err) {
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!params?.id) return;
    const fetchData = async () => {
      refreshData(params?.id);
      const res = await getHeaders({}, params?.id, "", true);
      setHeaders(res);
    };
    fetchData();
  }, [params?.id, location.search]);

  const onSelectRoom = async (selectedOption) => {
    console.log("selectedOption", selectedOption);
    setCheckoutErr(null)
    try {
      if (checkoutLoading) {
        return;
      }
      setCheckoutLoading(true);
      let checkoutPayload = {}
      // When booking straight from the search listing, the option carries its
      // own vendor + context (fromSearch); otherwise use the detail-page vendor.
      const fromSearch = selectedOption?.fromSearch === true;
      const isOwn = selectedOption?.vendor === "DAHRTOURS" || String(selectedOption?.hotelCode || params.id || "").startsWith("DAHR-");
      const effVendor = isOwn ? "DAHRTOURS" : (fromSearch ? (selectedOption?.vendor || "RATEHAWK") : vendor);
      if (isOwn) {
        checkoutPayload = {
          checkinDate: moment(fromSearch ? selectedOption?.checkinDate : checkinDate).format("YYYY-MM-DD"),
          checkoutDate: moment(fromSearch ? selectedOption?.checkoutDate : checkoutDate).format("YYYY-MM-DD"),
          cityId: fromSearch ? (selectedOption?.cityId ?? selectedOption?.city) : city,
          nationalityId: fromSearch ? (selectedOption?.nationalityId ?? selectedOption?.nationality) : nationality,
          commonId: fromSearch ? selectedOption?.commonId : commonId,
          residence: fromSearch ? selectedOption?.residence : residence,
          roomInfo: (fromSearch ? selectedOption?.roomInfo : roomInfo || []).map((r, i) => {
            const roomSrNo = String(selectedOption?.roomSrNo).split(",");
            return { ...r, roomSrNo: roomSrNo?.[i], rateKey: selectedOption?.rateKey?.[i] };
          }),
          hotelCode: fromSearch ? selectedOption?.hotelCode : params.id,
          vendor: "DAHRTOURS",
          hash: selectedOption?.rateKey?.[0],
          fromSearch: selectedOption?.fromSearch ?? false,
        };
      } else if(effVendor === 'HOTELRACK'){
        const hrRoomInfo = fromSearch ? selectedOption?.roomInfo : roomInfo;
        checkoutPayload = {
          checkinDate: moment(fromSearch ? selectedOption?.checkinDate : checkinDate).format("YYYY-MM-DD"),
          checkoutDate: moment(fromSearch ? selectedOption?.checkoutDate : checkoutDate).format("YYYY-MM-DD"),
          cityId: fromSearch ? (selectedOption?.cityId ?? selectedOption?.city) : (hrCityId || city),
          nationalityId: fromSearch ? (selectedOption?.nationalityId ?? selectedOption?.nationality) : nationality,
          roomInfo: (hrRoomInfo || []).map((r, i) => {
            const roomSrNo = String(selectedOption?.roomSrNo).split(",");
            return {
              ...r,
              roomSrNo: roomSrNo?.[i],
              rateKey: selectedOption?.rateKey?.[i],
            };
          }),
          hotelCode: fromSearch ? selectedOption?.hotelCode : params.id,
          tokenId: fromSearch ? selectedOption?.tokenId : details?.hotelDetails?.tokenId,
          hotelKey: selectedOption?.hotelKey,
          vendor: "HOTELRACK",
          commonId: fromSearch ? selectedOption?.commonId : commonId,
          residence: fromSearch ? selectedOption?.residence : residence
        };
      } else {
        checkoutPayload = {
          checkinDate: moment(checkinDate).format("YYYY-MM-DD"),
          checkoutDate: moment(checkoutDate).format("YYYY-MM-DD"),
          cityId: city,
          nationalityId: nationality,
          commonId,
          residence: residence,
          roomInfo: roomInfo.map((r, i) => {
            const roomSrNo = String(selectedOption?.roomSrNo).split(",");
            return {
              ...r,
              roomSrNo: roomSrNo?.[i],
              rateKey: selectedOption?.rateKey?.[i],
            };
          }),
          hotelCode: params.id,
          vendor : "RATEHAWK" , hash : selectedOption?.rateKey[0] ,
          fromSearch : selectedOption?.fromSearch ?? false 
        }
        if (selectedOption?.fromSearch) {
          checkoutPayload = {
            ...checkoutPayload, commonId: selectedOption?.commonId,
            checkinDate: moment(selectedOption?.checkinDate).format("YYYY-MM-DD"),
            checkoutDate: moment(selectedOption?.checkoutDate).format("YYYY-MM-DD"),
            cityId: selectedOption?.city,
            nationalityId: selectedOption?.nationality,
            commonId : selectedOption?.commonId,
            residence: selectedOption?.residence,
            roomInfo: selectedOption?.roomInfo.map((r, i) => {
              const roomSrNo = String(selectedOption?.roomSrNo).split(",");
              return {
                ...r,
                roomSrNo: roomSrNo?.[i],
                rateKey: selectedOption?.rateKey?.[i],
              };
            }),
            hotelCode: selectedOption.hotelCode,
          }
        }
      }
      const response = await ApiCall(
        "POST",
        "/hotels/checkout",
        { ...checkoutPayload, activeCurrency: agentActiveCurrency }
      );
      if (response?.data?.data?.saved?._id) {
        const checkoutQuery = isOwn
          ? `type=${toUrlVendor(effVendor)}`
          : `type=${toUrlVendor(effVendor)}&commonId=${response?.data?.data?.resultSet?.commonId}`;
        navigate(`/hotels/checkout/${response?.data?.data?.saved?._id}?${checkoutQuery}`);
      }
      else{
        setCheckoutErr(selectedOption?.rateKey?.[0] ?? selectedOption?.search_hash)
      }
    } catch (error) {
      setCheckoutErr(selectedOption?.rateKey?.[0] ?? selectedOption?.search_hash)
    } finally {
      setCheckoutLoading(false);
    }
  };
  return {
    loading,
    details,
    selectedOption,
    setSelectedOption,
    onSelectRoom,
    header,
    checkoutLoading,
    checkoutErr,
    detailError,
    searchQuery: {
      city,
      hid,
      ratings,
      nationality,
      countryOfResidence,
      roomInfo,
      checkinDate,
      checkoutDate,
      fsc,
    },
  };
};